import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUserPlus, FaSignInAlt } from "react-icons/fa";

import LoginModal from "./LoginModal/LoginModal";
import RegisterModal from "./RegisterModal/RegisterModal";
import InvestorFormModal from "./RegisterModal/InvestorForm";
import BrandFormModal from "./RegisterModal/BrandFormModal";
import PartnerFormModal from "./RegisterModal/PartnerFormModal";
import LeasingFormModal from "./RegisterModal/LeasingFormModal";

import "./design/Header.css";
import logo from "../assets/logo/franmax logo white.png";

const servicesSubmenu = [
  { name: "Sell Business", path: "/sell-business" },
  { name: "Lease Property", path: "/lease-property" },
  { name: "Marketing", path: "/marketing" },
];

const joinUsSubmenu = [
  { name: "Become Partner", path: "/partner" },
  { name: "Career", path: "/career" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  const [showLogin, setShowLogin] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showLeasingModal, setShowLeasingModal] = useState(false);

  const categoryId = 1;

  const [activeDropdown, setActiveDropdown] = useState(null);
  const servicesDropdownRef = useRef(null);
  const joinUsDropdownRef = useRef(null);

  // Hard reload function for specific links
  const hardReloadNavigation = (path) => {
    window.location.href = path;
  };

  const handleOpenRegister = useCallback(() => {
    setShowLogin(false);
    setIsRegisterOpen(true);
    setMenuOpen(false);
  }, []);

  const handleOpenLogin = useCallback(() => {
    setIsRegisterOpen(false);
    setShowLogin(true);
    setMenuOpen(false);
  }, []);

  const handleOpenInvestor = useCallback(() => {
    setIsRegisterOpen(false);
    setShowInvestorModal(true);
  }, []);

  const handleOpenBrand = useCallback(() => {
    setIsRegisterOpen(false);
    setShowBrandModal(true);
  }, []);

  const handleOpenPartner = useCallback(() => {
    setIsRegisterOpen(false);
    setShowPartnerModal(true);
  }, []);

  const handleOpenLeasing = useCallback(() => {
    setIsRegisterOpen(false);
    setShowLeasingModal(true);
  }, []);

  const toggleDropdown = useCallback((dropdownName) => {
    setActiveDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target) &&
        joinUsDropdownRef.current &&
        !joinUsDropdownRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className="top-bar">
        <div className="top-left">
          <Link to="/">
            <img src={logo} alt="Franmax Logo" className="logo-img" />
          </Link>
        </div>

        {/* Hamburger */}
        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          ☰
        </div>

        {/* Overlay */}
        {menuOpen && (
          <div className="menu-overlay" onClick={() => setMenuOpen(false)} />
        )}

        <nav className={`nav-center ${menuOpen ? "open" : ""}`}>
          {/* THESE 4 LINKS WILL CAUSE HARD RELOAD */}
          <Link 
            to="/" 
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              hardReloadNavigation('/');
            }}
          >🏠</Link>
          
          <Link 
            to="/consulting" 
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              hardReloadNavigation('/consulting');
            }}
          >Start a Business</Link>
          
          <Link 
            to="/franchise" 
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              hardReloadNavigation('/franchise');
            }}
          >Expand Your Business</Link>
          
          <Link 
            to={`/franchises/category/${categoryId}`} 
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              hardReloadNavigation(`/franchises/category/${categoryId}`);
            }}
          >Explore Franchise</Link>

          {/* THIS LINK WILL NOT CAUSE HARD RELOAD (opens modal) */}
          <Link
            onClick={() => {
              handleOpenBrand();
              setMenuOpen(false);
            }}
            className="nav-link-button"
          >
            List Your Brand
          </Link>

          <div className="dropdown-wrapper" ref={servicesDropdownRef}>
            <span className="dropdown-trigger" onClick={() => toggleDropdown("services")}>
              Services ▾
            </span>
            {activeDropdown === "services" && (
              <div className="dropdown-menu">
                {servicesSubmenu.map((item, i) => (
                  <Link
                    key={i}
                    to={item.path}
                    className="dropdown-item"
                    onClick={() => {
                      setActiveDropdown(null);
                      setMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="dropdown-wrapper" ref={joinUsDropdownRef}>
            <span className="dropdown-trigger" onClick={() => toggleDropdown("join-us")}>
              Join Us ▾
            </span>
            {activeDropdown === "join-us" && (
              <div className="dropdown-menu">
                {joinUsSubmenu.map((item, i) => (
                  <Link
                    key={i}
                    to={item.path}
                    className="dropdown-item"
                    onClick={() => {
                      setActiveDropdown(null);
                      setMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Register/Login Buttons */}
          <div className="mobile-auth-buttons">
            <button 
              className="action-button register" 
              onClick={handleOpenRegister}
              style={{ color: 'white' }}
            >
              <FaUserPlus /> <span style={{ color: 'white' }}>Register</span>
            </button>
            <button 
              className="action-button login" 
              onClick={handleOpenLogin}
              style={{ color: 'white' }}
            >
              <FaSignInAlt /> <span style={{ color: 'white' }}>Login</span>
            </button>
          </div>
        </nav>

        <nav className="user-actions">
          <button className="action-button register" onClick={handleOpenRegister}>
            <FaUserPlus /> <span>Register</span>
          </button>
          <button className="action-button login" onClick={handleOpenLogin}>
            <FaSignInAlt /> <span>Login</span>
          </button>
        </nav>
      </header>

      {/* Modals */}
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} openRegister={handleOpenRegister} />
      <RegisterModal isOpen={isRegisterOpen} onClose={() => setIsRegisterOpen(false)} openLogin={handleOpenLogin} openInvestor={handleOpenInvestor} openBrand={handleOpenBrand} openPartner={handleOpenPartner} openLeasing={handleOpenLeasing} />
      <InvestorFormModal isOpen={showInvestorModal} onClose={() => setShowInvestorModal(false)} onBack={() => { setShowInvestorModal(false); setIsRegisterOpen(true); }} openLogin={handleOpenLogin} />
      <BrandFormModal isOpen={showBrandModal} onClose={() => setShowBrandModal(false)} onBack={() => { setShowBrandModal(false); setIsRegisterOpen(true); }} openLogin={handleOpenLogin} />
      <PartnerFormModal isOpen={showPartnerModal} onClose={() => setShowPartnerModal(false)} onBack={() => { setShowPartnerModal(false); setIsRegisterOpen(true); }} openLogin={handleOpenLogin} />
      <LeasingFormModal isOpen={showLeasingModal} onClose={() => setShowLeasingModal(false)} onBack={() => { setShowLeasingModal(false); setIsRegisterOpen(true); }} />
    </>
  );
}